<script setup>
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import {
    getDirector,
    useEmployeesStore,
    useTariffStore,
    useUserStore,
} from "@/entities";
import { IconChevronRight, PROFILE_ROUTE_NAMES } from "@/shared";

import menuConstants from "../config/menuConstants";
import { useSidebarStore } from "../model/useSidebarStore";

import SidebarLink from "./SidebarLink.vue";

// store
const sidebarStore = useSidebarStore();
const { fetchMenuLinks } = sidebarStore;
const { menuLinks, isCollapsed } = storeToRefs(sidebarStore);
const userStore = useUserStore();
const employeesStore = useEmployeesStore();
const { tariff } = storeToRefs(useTariffStore());
const router = useRouter();

onMounted(() => {
    if (menuLinks.value.length === 0) fetchMenuLinks();
});

// Разделы Магазин/Мастерская доступны только при наличии модуля в тарифе.
// Бэк отдаёт их пункты меню всем директорам, поэтому доступ определяем по
// активному тарифу: нет модуля — пункт показывается заблокированным с
// подсказкой «доступно в Профи» (shouldDisplayWithoutAccess).
const TARIFF_GATED_MENU = {
    MENU_STOCK: "haveAccessStore",
    MENU_REPAIR: "haveAccessRepairService",
};

// access
const checkAccess = (desiredConst) => {
    if (!menuLinks.value) return false;

    const tariffFlag = TARIFF_GATED_MENU[desiredConst];

    if (tariffFlag && !tariff.value?.[tariffFlag]) return false;

    return menuLinks.value.some((menuLink) => {
        return menuLink.const === desiredConst;
    });
};

// ─── Профиль для футера сайдбара ────────────────────────────────────────────
// Подтягиваем ФИО из getDirector / employeesStore.getOne — один раз на сессию.
// Пока не подгрузилось — показываем @login как fallback.
const profileInfo = ref(null);

const fetchProfileInfo = async () => {
    if (!userStore.profileId) return;

    try {
        if (userStore.isDirector) {
            const { director } = await getDirector(userStore.profileId);

            profileInfo.value = director;
        } else {
            profileInfo.value = await employeesStore.getOne(
                userStore.profileId
            );
        }
    } catch (e) {
        // Падать не страшно — отрендерим fallback по userLogin
        console.error("Sidebar profile fetch failed:", e);
    }
};

const fio = computed(() => {
    return profileInfo.value?.human?.fio || "";
});

const displayName = computed(() => {
    if (fio.value) {
        // "Тестовый Пользователь 1" → "Тестовый П."
        const parts = fio.value.trim().split(/\s+/).filter(Boolean);

        if (parts.length >= 2) {
            return `${parts[0]} ${parts[1][0]}.`;
        }

        return parts[0];
    }

    const login = userStore.userLogin || "—";

    return login.split("@")[0];
});

const initials = computed(() => {
    const source = fio.value || userStore.userLogin || "?";
    const parts = source.trim().split(/\s+/).filter(Boolean);

    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }

    return parts[0]?.[0]?.toUpperCase() || "?";
});

const role = computed(() => {
    return userStore.isDirector ? "Администратор" : "Сотрудник";
});

const goToProfile = () => {
    router.push({ name: PROFILE_ROUTE_NAMES.PROFILE });
};

// collapse
const sidebarRef = ref(null);
const checkScrollbarExistence = () => {
    return sidebarRef.value.scrollHeight > sidebarRef.value.clientHeight;
};

const setSidebarWidthDependingOnScrollbar = async () => {
    await nextTick();

    const root = document.querySelector(":root");

    if (checkScrollbarExistence()) {
        root.style.setProperty(
            "--sidebar-collapsed-width",
            "calc(var(--sidebar-collapsed-width-const) + var(--default-scrollbar-width))"
        );
    } else {
        root.style.setProperty(
            "--sidebar-collapsed-width",
            "var(--sidebar-collapsed-width-const)"
        );
    }
};

onMounted(() => {
    setSidebarWidthDependingOnScrollbar();
    fetchProfileInfo();
});
</script>

<template>
    <aside
        v-if="menuLinks"
        class="the-sidebar"
        :class="{
            'the-sidebar--collapsed': isCollapsed,
            'the-sidebar--expanded': !isCollapsed,
        }"
        ref="sidebarRef"
    >
        <div class="the-sidebar__nav">
            <SidebarLink
                v-for="(link, key) in menuConstants"
                :key="key"
                :link-meta="link"
                :has-access="checkAccess(key)"
                :is-collapsed="isCollapsed"
                @toggle-children="setSidebarWidthDependingOnScrollbar"
            />
        </div>
        <button
            v-if="!isCollapsed"
            type="button"
            class="sidebar-profile"
            @click="goToProfile"
        >
            <span class="sidebar-profile__avatar">{{ initials }}</span>
            <span class="sidebar-profile__text">
                <span class="sidebar-profile__name">{{ displayName }}</span>
                <span class="sidebar-profile__role">{{ role }}</span>
            </span>
            <IconChevronRight class="sidebar-profile__chevron" />
        </button>
        <button
            v-else
            type="button"
            class="sidebar-profile sidebar-profile--collapsed"
            :title="displayName"
            @click="goToProfile"
        >
            <span class="sidebar-profile__avatar">{{ initials }}</span>
        </button>
    </aside>
</template>

<style scoped lang="scss">
.the-sidebar {
    --sidebar-link-icon-width: 16px;
    --sidebar-link-icon-height: 14px;
    position: fixed;
    top: var(--header-height);
    left: 0;

    z-index: var(--zi-sidebar);

    display: flex;

    flex-direction: column;

    width: var(--sidebar-width);
    height: calc(100vh - var(--header-height));
    padding-top: 12px;

    background-color: var(--sidebar-background);

    transition: all var(--sidebar-collapse-transition-duration);

    @media (max-width: 576px) {
        transform: translateX(-100%);
    }

    &--expanded {
        @media (max-width: 576px) {
            transform: translateX(0);
        }
    }

    &__nav {
        flex: 1;

        overflow: hidden auto;
    }
}

.sidebar-profile {
    display: flex;

    flex-shrink: 0;

    gap: 10px;

    align-items: center;

    width: calc(100% - 12px);
    padding: 10px 10px 10px 8px;
    margin: 6px;

    color: #fff;
    text-align: left;

    cursor: pointer;

    background: transparent;

    border: none;
    border-radius: 8px;

    transition: background-color 0.15s;

    &:hover {
        background-color: rgba(255 255 255 / 6%);
    }

    &--collapsed {
        justify-content: center;

        padding: 10px 0;
    }

    &__avatar {
        display: inline-flex;

        flex-shrink: 0;

        align-items: center;
        justify-content: center;

        width: 32px;
        height: 32px;

        font-size: 12px;
        font-weight: 600;

        color: #fff;

        background: linear-gradient(135deg, #14b8a6, #6366f1);
        border-radius: 50%;
    }

    &__text {
        display: flex;

        flex: 1;

        flex-direction: column;

        gap: 1px;

        min-width: 0;
    }

    &__name {
        overflow: hidden;

        font-size: 13px;
        font-weight: 600;

        color: #fff;

        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__role {
        font-size: 11px;

        color: rgba(255 255 255 / 50%);
    }

    &__chevron {
        flex-shrink: 0;

        width: 10px;
        height: 10px;

        color: rgba(255 255 255 / 40%);
    }
}
</style>
